/**
 * ============================================================================
 * USER COMPONENT: New Bulk Phone Campaign Modal
 * ============================================================================
 * Purpose:
 *   Faithful implementation of the "New Bulk Phone Campaign" modal:
 *   - Campaign Title & Optional Description
 *   - Destination URL with Quick Tag `+{phone}` helper
 *   - Upload Phone Numbers File (Excel / CSV) with Sample Excel template download
 *   - Drag & drop zone with auto phone column detection & contact counter
 *   - Link Expiration dropdown (Never Expire, 24 Hours, 7 Days, 30 Days, Custom)
 *   - Instant generation of tamper-proof short links per contact
 *   - Export results to Excel (.xlsx) / CSV for WhatsApp broadcast campaigns
 *   - Mounted directly to document.body via createPortal to prevent layout clipping
 * ============================================================================
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  PhoneIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

import tinyUrlService from "../../services/tinyUrlService";

export default function BulkCreateTinyUrlDrawer({
  isOpen,
  onClose,
  onSuccess,
}) {
  const frontendBase = window.location.origin;

  // Form Fields matching screenshot
  const [campaignTitle, setCampaignTitle] = useState("");
  const [description, setDescription] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [expirationPreset, setExpirationPreset] = useState("never");
  const [customDate, setCustomDate] = useState("");

  // File Upload & Parsed Contacts State
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [rawSheetRows, setRawSheetRows] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedPhoneCol, setSelectedPhoneCol] = useState("");
  const [selectedNameCol, setSelectedNameCol] = useState("");
  const [parsedContacts, setParsedContacts] = useState([]);

  // Submission & Results State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationResults, setGenerationResults] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);

  // Keyboard Escape listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen && !isSubmitting) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSubmitting]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleReset = () => {
    setCampaignTitle("");
    setDescription("");
    setDestinationUrl("");
    setExpirationPreset("never");
    setCustomDate("");
    setUploadedFile(null);
    setRawSheetRows([]);
    setAvailableColumns([]);
    setSelectedPhoneCol("");
    setSelectedNameCol("");
    setParsedContacts([]);
    setFileError(null);
    setGenerationResults(null);
  };

  // ─── Quick Tag: Insert `+{phone}` at Cursor / Append ───
  const handleInsertPhoneTag = () => {
    if (!destinationUrl.trim()) {
      setDestinationUrl("https://wa.me/{phone}");
      return;
    }

    const input = urlInputRef.current;
    if (input) {
      const start = input.selectionStart || destinationUrl.length;
      const end = input.selectionEnd || destinationUrl.length;
      const newUrl =
        destinationUrl.substring(0, start) +
        "{phone}" +
        destinationUrl.substring(end);
      setDestinationUrl(newUrl);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + 7, start + 7);
      }, 50);
    } else {
      if (!destinationUrl.includes("{phone}")) {
        if (destinationUrl.endsWith("=")) {
          setDestinationUrl(destinationUrl + "{phone}");
        } else if (destinationUrl.includes("?")) {
          setDestinationUrl(destinationUrl + "&phone={phone}");
        } else {
          setDestinationUrl(destinationUrl + "/{phone}");
        }
      }
    }
  };

  // ─── Download Sample Excel (.xlsx) ───
  const handleDownloadSampleExcel = () => {
    const sampleData = [
      {
        "Phone Number": "919876543210",
        "Customer Name": "Rahul Sharma",
        "Custom Code (Optional)": "",
      },
      {
        "Phone Number": "919876543211",
        "Customer Name": "Pooja Patel",
        "Custom Code (Optional)": "",
      },
      {
        "Phone Number": "919876543212",
        "Customer Name": "Amit Kumar",
        "Custom Code (Optional)": "VIP123",
      },
      {
        "Phone Number": "919876543213",
        "Customer Name": "Sneha Roy",
        "Custom Code (Optional)": "",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "sample_phone_numbers.xlsx");
  };

  // ─── Extract Contacts from Sheet Data ───
  const extractContactsFromRows = useCallback((rows, phoneCol, nameCol) => {
    if (!rows || rows.length === 0 || !phoneCol) {
      setParsedContacts([]);
      return;
    }

    const contacts = [];
    const seenPhones = new Set();

    rows.forEach((row, idx) => {
      const rawPhone = String(row[phoneCol] || "").trim();
      const cleanPhone = rawPhone.replace(/[^0-9]/g, "");

      if (cleanPhone && cleanPhone.length >= 7 && cleanPhone.length <= 16) {
        if (!seenPhones.has(cleanPhone)) {
          seenPhones.add(cleanPhone);
          const name = nameCol ? String(row[nameCol] || "").trim() : "";
          const customCodeKey = Object.keys(row).find((k) =>
            /code|custom|short/i.test(k)
          );
          const customCode = customCodeKey
            ? String(row[customCodeKey] || "").trim().toUpperCase()
            : "";

          contacts.push({
            id: idx + 1,
            phone: cleanPhone,
            name: name || `Contact ${contacts.length + 1}`,
            customCode,
          });
        }
      }
    });

    setParsedContacts(contacts);
  }, []);

  // When selected column changes, re-extract
  useEffect(() => {
    if (rawSheetRows.length > 0 && selectedPhoneCol) {
      extractContactsFromRows(rawSheetRows, selectedPhoneCol, selectedNameCol);
    }
  }, [selectedPhoneCol, selectedNameCol, rawSheetRows, extractContactsFromRows]);

  // ─── Process Uploaded Spreadsheet ───
  const processFile = useCallback(
    (file) => {
      if (!file) return;
      const isExcelOrCsv = /\.(xlsx|xls|csv)$/i.test(file.name);
      if (!isExcelOrCsv) {
        setFileError("Please upload a valid .xlsx, .xls, or .csv file.");
        return;
      }

      setUploadedFile({
        name: file.name,
        size: (file.size / 1024).toFixed(1) + " KB",
      });
      setIsParsing(true);
      setFileError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          if (!sheetName) throw new Error("No worksheets found in uploaded file.");

          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          if (!json || json.length === 0) {
            setFileError("The uploaded file contains no data rows.");
            setParsedContacts([]);
            setIsParsing(false);
            return;
          }

          const headers = Object.keys(json[0] || {});
          let detectedPhoneCol = headers.find((h) =>
            /phone|mobile|number|contact|recipient|whatsapp|cell/i.test(h)
          );
          const detectedNameCol = headers.find((h) =>
            /name|customer|user|client|person/i.test(h)
          );

          if (!detectedPhoneCol) {
            // Check numeric 10-15 digits
            for (const h of headers) {
              const sample = String(json[0][h] || "").replace(/[^0-9]/g, "");
              if (sample.length >= 10 && sample.length <= 15) {
                detectedPhoneCol = h;
                break;
              }
            }
          }

          if (!detectedPhoneCol && headers.length > 0) {
            detectedPhoneCol = headers[0];
          }

          setRawSheetRows(json);
          setAvailableColumns(headers);
          setSelectedPhoneCol(detectedPhoneCol || "");
          setSelectedNameCol(detectedNameCol || "");

          extractContactsFromRows(json, detectedPhoneCol, detectedNameCol);
        } catch (err) {
          console.error("Failed to parse file:", err);
          setFileError("Failed to parse file: " + err.message);
          setParsedContacts([]);
        } finally {
          setIsParsing(false);
        }
      };

      reader.onerror = () => {
        setFileError("Could not read uploaded file.");
        setIsParsing(false);
      };

      reader.readAsArrayBuffer(file);
    },
    [extractContactsFromRows]
  );

  // Drag and Drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  // ─── Submit Campaign Generation ───
  const handleGenerateCampaign = async (e) => {
    e.preventDefault();

    if (!campaignTitle.trim()) {
      Swal.fire({
        title: "Campaign Title Required",
        text: "Please enter a Campaign Title (e.g. Diwali WhatsApp Blast).",
        icon: "warning",
        confirmButtonColor: "#7c83fd",
      });
      return;
    }

    if (!destinationUrl.trim()) {
      Swal.fire({
        title: "Destination URL Required",
        text: "Please provide the Original Destination URL for the campaign.",
        icon: "warning",
        confirmButtonColor: "#7c83fd",
      });
      return;
    }

    if (parsedContacts.length === 0) {
      Swal.fire({
        title: "No Phone Numbers Found",
        text: "Please upload an Excel or CSV file containing phone numbers.",
        icon: "warning",
        confirmButtonColor: "#7c83fd",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        title: campaignTitle.trim(),
        description: description.trim(),
        originalDestinationUrl: destinationUrl.trim(),
        expirationPreset,
        expirationDate: expirationPreset === "custom" ? customDate : null,
        contacts: parsedContacts.map((c) => ({
          phoneNumber: c.phone,
          name: c.name || "",
          customCode: c.customCode || undefined,
        })),
      };

      const res = await tinyUrlService.createCampaign(payload);
      const campaignData = res?.data?.data || res?.data || res;

      if (campaignData && (campaignData.code || campaignData.contacts)) {
        const createdContacts = campaignData.contacts || [];
        setGenerationResults({
          campaign: campaignData,
          created: createdContacts.map((c) => ({
            code: c.code || `${campaignData.code}/${c.token}`,
            tinyUrl: c.fullShortUrl || `${frontendBase}/${campaignData.code}/${c.token}`,
            originalDestinationUrl: campaignData.originalDestinationUrl,
            phone: c.phoneNumber,
            name: c.name,
            expirationDate: campaignData.expirationDate,
            createdAt: campaignData.createdAt,
          })),
          errors: [],
          total: createdContacts.length,
          createdCount: createdContacts.length,
          failedCount: 0,
        });

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      console.error("Bulk Campaign Generation Failed:", err);
      Swal.fire({
        title: "Generation Failed",
        text: err.response?.data?.error || err.message || "An unexpected error occurred",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Export Generated Results ───
  const handleExportGeneratedLinks = (format = "xlsx") => {
    if (!generationResults?.created || generationResults.created.length === 0) return;

    const exportData = generationResults.created.map((item, idx) => {
      // Find corresponding contact
      const contact = parsedContacts[idx] || {};
      return {
        "Row #": idx + 1,
        "Campaign Title": campaignTitle,
        "Phone Number": contact.phone || "",
        "Customer Name": contact.name || "",
        "Short URL": item.tinyUrl || `${frontendBase}/${item.code}`,
        "Short Code": item.code,
        "Original Destination URL": item.originalDestinationUrl,
        "Expiration": item.expirationDate
          ? new Date(item.expirationDate).toLocaleString()
          : "Never",
        "Created At": item.createdAt
          ? new Date(item.createdAt).toLocaleString()
          : new Date().toLocaleString(),
      };
    });

    if (format === "xlsx") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Campaign Links");
      XLSX.writeFile(wb, `${campaignTitle.replace(/\s+/g, "_")}_Links_${Date.now()}.xlsx`);
    } else {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${campaignTitle.replace(/\s+/g, "_")}_Links_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCopy = (text, code) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleCopyAll = () => {
    if (!generationResults?.created) return;
    const allUrls = generationResults.created
      .map((it) => it.tinyUrl || `${frontendBase}/${it.code}`)
      .join("\n");
    navigator.clipboard.writeText(allUrls);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  if (!isOpen) return null;

  return (
    typeof document !== "undefined" &&
    createPortal(
      <>
        {/* Backdrop Overlay */}
        <div
          className="fixed inset-0 z-[99998] bg-black/60 backdrop-blur-xs transition-opacity duration-200 animate-in fade-in"
          onClick={handleClose}
          aria-hidden="true"
        />

        {/* Centered Modal Container */}
        <div className="fixed inset-0 z-[99999] overflow-y-auto flex items-center justify-center p-4 sm:p-6">
          <div
            className="relative w-full max-w-2xl bg-white dark:bg-[#151824] rounded-3xl shadow-2xl border border-gray-200/80 dark:border-gray-800 my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ─── MODAL HEADER ─── */}
            <div className="px-6 sm:px-8 pt-7 pb-4 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3.5 min-w-0">
                {/* Purple/Blue Gradient Rounded Icon */}
                <div className="w-12 h-12 rounded-2xl bg-[#525fe1] text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-[#525fe1]/25">
                  <PhoneIcon className="w-6 h-6 stroke-[2]" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                    New Bulk Phone Campaign
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    Upload contacts to generate tamper-proof links (e.g. {frontendBase.replace(/^https?:\/\//, "")}/zPn82c/k8X9mP)
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                title="Close"
              >
                <XMarkIcon className="w-5 h-5 stroke-[2]" />
              </button>
            </div>

            {/* ─── MODAL CONTENT BODY ─── */}
            <div className="px-6 sm:px-8 py-3 space-y-4 text-xs">
              {/* ─── SCREEN 1: SUCCESS & EXPORT RESULTS ─── */}
              {generationResults ? (
                <div className="space-y-5 animate-in fade-in py-2">
                  <div className="p-6 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 text-center space-y-2">
                    <div className="w-12 h-12 mx-auto rounded-2xl bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/25">
                      <CheckCircleIcon className="w-7 h-7 stroke-[2.5]" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Campaign Generated Successfully!
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Generated <b className="text-emerald-600 font-bold">{generationResults.createdCount} tamper-proof links</b> for your WhatsApp broadcast.
                    </p>

                    {/* Export Action Buttons */}
                    <div className="pt-2 flex flex-wrap items-center justify-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => handleExportGeneratedLinks("xlsx")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-md shadow-emerald-600/20 cursor-pointer transition-all"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Download Campaign Excel (.xlsx)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleExportGeneratedLinks("csv")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-xs cursor-pointer transition-colors shadow-xs"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 text-[#3a57e8]" />
                        Download CSV
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyAll}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-xs cursor-pointer transition-colors shadow-xs"
                      >
                        {copiedAll ? (
                          <>
                            <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                            Copied All!
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="w-4 h-4 text-gray-400" />
                            Copy All URLs
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Links Preview Table */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs overflow-hidden">
                    <div className="p-3 border-b border-gray-100 dark:border-gray-700 font-bold text-gray-900 dark:text-white text-xs">
                      Generated Links Preview ({generationResults.created.length})
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
                      {generationResults.created.map((item, idx) => {
                        const fullUrl = item.tinyUrl || `${frontendBase}/${item.code}`;
                        const isCopied = copiedCode === item.code;
                        return (
                          <div
                            key={item._id || idx}
                            className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50/60 dark:hover:bg-gray-750/30 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-xs text-[#3a57e8] bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded border border-blue-200/50">
                                  {item.code}
                                </span>
                                <span className="font-semibold text-gray-900 dark:text-white truncate">
                                  {item.title}
                                </span>
                              </div>
                              <div className="mt-0.5 font-mono text-[11px] text-gray-400 truncate">
                                {fullUrl} &rarr; {item.originalDestinationUrl}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCopy(fullUrl, item.code)}
                              className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 cursor-pointer"
                              title="Copy Short URL"
                            >
                              {isCopied ? (
                                <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <ClipboardDocumentIcon className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── SCREEN 2: MAIN FORM (MATCHING USER SCREENSHOT) ─── */
                <form id="bulk-phone-campaign-form" onSubmit={handleGenerateCampaign} className="space-y-4">
                  {/* Row 1: CAMPAIGN TITLE * & DESCRIPTION (OPTIONAL) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                        Campaign Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Diwali WhatsApp Blast"
                        value={campaignTitle}
                        onChange={(e) => setCampaignTitle(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-750 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7c83fd] transition-all"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                        Description (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. VIP Offer 20% discount"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-750 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7c83fd] transition-all"
                      />
                    </div>
                  </div>

                  {/* Row 2: ORIGINAL DESTINATION URL * with Quick Tag: +{phone} */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Original Destination URL <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                        <span>Quick Tag:</span>
                        <button
                          type="button"
                          onClick={handleInsertPhoneTag}
                          className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/60 text-[#3a57e8] dark:text-blue-400 font-mono font-bold text-[11px] border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors cursor-pointer"
                          title="Click to insert {phone} tag at cursor position"
                        >
                          +&#123;phone&#125;
                        </button>
                      </div>
                    </div>

                    <input
                      ref={urlInputRef}
                      type="text"
                      placeholder="e.g. https://wa.me/{phone}?text=Hello or https://api.whatsapp.com/send?phone="
                      value={destinationUrl}
                      onChange={(e) => setDestinationUrl(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-750 text-xs text-gray-900 dark:text-white placeholder-gray-400 font-mono focus:outline-none focus:ring-2 focus:ring-[#7c83fd] transition-all"
                      required
                    />

                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1">
                      <QuestionMarkCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      Use <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded font-mono text-[#3a57e8] font-bold">&#123;phone&#125;</code> to dynamically inject each customer&apos;s decrypted number!
                    </p>
                  </div>

                  {/* Row 3: UPLOAD PHONE NUMBERS FILE * with Sample Excel */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Upload Phone Numbers File <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={handleDownloadSampleExcel}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#3a57e8] hover:underline cursor-pointer"
                      >
                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        Sample Excel
                      </button>
                    </div>

                    {/* Drag and Drop Zone */}
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-2xl p-6 sm:p-7 text-center cursor-pointer transition-all duration-200 ${
                        dragActive
                          ? "border-[#7c83fd] bg-indigo-50/60 dark:bg-indigo-950/40 scale-[1.01]"
                          : uploadedFile
                          ? "border-emerald-400 bg-emerald-50/20 dark:bg-emerald-950/20"
                          : "border-gray-300 dark:border-gray-600 hover:border-[#7c83fd] hover:bg-gray-50/60 dark:hover:bg-gray-800/40"
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx, .xls, .csv"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            processFile(e.target.files[0]);
                          }
                        }}
                        className="hidden"
                      />

                      <div className="w-12 h-12 mx-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 flex items-center justify-center mb-2 shadow-xs">
                        {isParsing ? (
                          <ArrowPathIcon className="w-5 h-5 animate-spin text-[#7c83fd]" />
                        ) : (
                          <ArrowUpTrayIcon className="w-5 h-5 stroke-[2]" />
                        )}
                      </div>

                      <p className="font-bold text-xs text-gray-800 dark:text-gray-200">
                        {uploadedFile
                          ? uploadedFile.name
                          : "Click to browse or drag & drop Excel / CSV"}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {uploadedFile ? uploadedFile.size : "Supports .xlsx, .xls, .csv"}
                      </p>
                    </div>

                    {/* File Error */}
                    {fileError && (
                      <div className="mt-2 p-2.5 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 flex items-center gap-2 text-[11px] text-red-600 dark:text-red-300">
                        <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span>{fileError}</span>
                      </div>
                    )}

                    {/* Contacts Detection Feedback Bar */}
                    {uploadedFile && parsedContacts.length > 0 && (
                      <div className="mt-2.5 p-3 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-600 font-bold flex-shrink-0" />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            <b className="text-emerald-700 dark:text-emerald-300">{parsedContacts.length} Phone Numbers</b> detected & ready
                          </span>
                        </div>

                        {availableColumns.length > 1 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <span className="text-gray-500">Phone Col:</span>
                            <select
                              value={selectedPhoneCol}
                              onChange={(e) => setSelectedPhoneCol(e.target.value)}
                              className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold cursor-pointer"
                            >
                              {availableColumns.map((col) => (
                                <option key={col} value={col}>
                                  {col}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Row 4: LINK EXPIRATION Dropdown */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                      Link Expiration
                    </label>
                    <select
                      value={expirationPreset}
                      onChange={(e) => setExpirationPreset(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-750 text-xs text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7c83fd] cursor-pointer"
                    >
                      <option value="never">Never Expire (Permanent)</option>
                      <option value="24h">24 Hours</option>
                      <option value="7d">7 Days</option>
                      <option value="30d">30 Days</option>
                      <option value="custom">Custom Date</option>
                    </select>

                    {expirationPreset === "custom" && (
                      <div className="mt-2">
                        <input
                          type="datetime-local"
                          value={customDate}
                          onChange={(e) => setCustomDate(e.target.value)}
                          className="w-full px-3.5 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-750 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#7c83fd]"
                          required={expirationPreset === "custom"}
                        />
                      </div>
                    )}
                  </div>
                </form>
              )}
            </div>

            {/* ─── MODAL FOOTER ─── */}
            <div className="px-6 sm:px-8 py-4 bg-gray-50/70 dark:bg-gray-850 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-3">
              {generationResults ? (
                <>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold cursor-pointer text-xs transition-colors shadow-xs"
                  >
                    Create Another Campaign
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 rounded-xl bg-[#525fe1] hover:bg-[#4350d6] text-white font-semibold shadow-md shadow-[#525fe1]/25 text-xs cursor-pointer transition-all"
                  >
                    Done & View Campaigns
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold cursor-pointer text-xs transition-colors shadow-xs"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    form="bulk-phone-campaign-form"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#7c83fd] to-[#8d72e1] hover:from-[#6b73fb] hover:to-[#7c60d9] text-white text-xs font-semibold shadow-md shadow-indigo-500/25 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        Generating Campaign...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4" />
                        Generate Campaign ({parsedContacts.length} Links)
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </>,
      document.body
    )
  );
}
